import { z } from 'zod';

import {
  CollectionRulesMatch,
  RuleConditionInputOperator,
} from '@nucleus-commerce/api-client/admin';

export const MAX_CONDITIONS = 20;

/**
 * One condition as the builder edits it: the value is a string (money as minor units, dates as
 * YYYY-MM-DD, booleans as "true" / "false") or a list of strings for in / not_in. The mapper
 * turns it into the typed scalar the API wants.
 */
export const ruleConditionFormSchema = z
  .object({
    field: z.string().min(1, { error: 'productRules:validation.fieldRequired' }),
    operator: z.enum(RuleConditionInputOperator),
    value: z.union([z.string(), z.array(z.string())]),
  })
  .refine((c) => (Array.isArray(c.value) ? c.value.length > 0 : c.value.trim() !== ''), {
    path: ['value'],
    error: 'productRules:validation.valueRequired',
  });

/** A rule set (`CollectionRules` on the wire): smart collections and schedule pools share it. */
export const rulesFormSchema = z.object({
  match: z.enum(CollectionRulesMatch),
  conditions: z
    .array(ruleConditionFormSchema)
    .max(MAX_CONDITIONS, { error: 'productRules:validation.conditionsMax' }),
});

export type RuleConditionForm = z.infer<typeof ruleConditionFormSchema>;
export type RulesForm = z.infer<typeof rulesFormSchema>;

/** What a form must hold for the builder: the rule set under `rules`. */
export interface RulesFormHost {
  rules: RulesForm;
}

export const emptyRules = (withCondition: boolean): RulesForm => ({
  match: 'all',
  conditions: withCondition ? [emptyCondition()] : [],
});

export const emptyCondition = (): RuleConditionForm => ({
  field: '',
  operator: 'equals',
  value: '',
});
